import { z } from "zod";
import { AssetVisibilitySchema } from "./asset-visibility.js";

export { type AssetVisibility, AssetVisibilitySchema } from "./asset-visibility.js";

export const APPLICATION_ICON_KEYS = [
  "activity",
  "archive",
  "badge-check",
  "banknote",
  "bell",
  "book-open",
  "bot",
  "box",
  "briefcase",
  "building-2",
  "calendar",
  "chart-line",
  "check-circle-2",
  "circle-dollar-sign",
  "clipboard",
  "clock",
  "cloud",
  "code-2",
  "database",
  "file-text",
  "globe-2",
  "heart",
  "house",
  "inbox",
  "layout-dashboard",
  "life-buoy",
  "link",
  "list-checks",
  "mail",
  "map",
  "message-square",
  "package",
  "panel-top",
  "pencil-ruler",
  "phone",
  "search",
  "settings",
  "shield-check",
  "shopping-bag",
  "shopping-cart",
  "sparkles",
  "star",
  "store",
  "tag",
  "ticket",
  "truck",
  "users",
  "wallet",
  "wrench",
] as const;

export const ApplicationIconKeySchema = z.enum(APPLICATION_ICON_KEYS);
export type ApplicationIconKey = z.infer<typeof ApplicationIconKeySchema>;

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.null(),
    z.boolean(),
    z.number(),
    z.string(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

export const ApplicationIdSchema = z.string().regex(/^app_[a-f0-9]{16}$/);
export const ApplicationActionNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/);
const ApplicationTitleSchema = z
  .string()
  .trim()
  .refine((value) => Array.from(value).length >= 1 && Array.from(value).length <= 120, {
    message: "Title must contain 1-120 characters",
  });

export const ApplicationSummarySchema = z
  .object({
    appId: ApplicationIdSchema,
    agentId: z.string().min(1),
    title: ApplicationTitleSchema,
    blueprintId: z.string().min(1).nullable(),
    blueprintTitle: z.string().trim().min(1).nullable(),
    icon: ApplicationIconKeySchema.optional(),
    visibility: AssetVisibilitySchema,
    /** Whether the reading viewer may change this application’s visibility. */
    viewerCanChangeVisibility: z.boolean(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict();

export const ApplicationDetailSchema = ApplicationSummarySchema.extend({
  html: z.string(),
  clientCode: z.string().optional(),
  createdByUserId: z.string().min(1).optional(),
  actionNames: z.array(ApplicationActionNameSchema).max(32),
}).strict();

export const ApplicationsListPayloadSchema = z.object({}).strict();
export const ApplicationsListDataSchema = z
  .object({ applications: z.array(ApplicationSummarySchema) })
  .strict();

export const ApplicationsGetPayloadSchema = z.object({ appId: ApplicationIdSchema }).strict();
export const ApplicationsGetDataSchema = z
  .object({ application: ApplicationDetailSchema })
  .strict();

export const ApplicationsRunActionPayloadSchema = z
  .object({
    appId: ApplicationIdSchema,
    actionName: ApplicationActionNameSchema,
    args: JsonValueSchema,
    triggeredByUserId: z.string().min(1),
  })
  .strict()
  .refine((value) => utf8ByteLength(JSON.stringify(value.args)) <= 262_144, {
    message: "Serialized action arguments must be at most 262144 UTF-8 bytes",
    path: ["args"],
  });

export const ApplicationsRunActionDataSchema = z.object({ result: JsonValueSchema }).strict();

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}
